import { Type } from '@angular/core';
import { Params } from '@angular/router';
import { Observable } from 'rxjs';

import { CustomModuleComponent } from '@usheru-hff/custom-page/feature-custom-page';
import {
  ApiResponse,
  CarouselParams,
  CarouselResponsiveOption,
  Channel,
  CompetitionConfig,
  Country,
  CountryCode,
  Crew,
  CustomMovie,
  Language,
  LanguageCode,
  Movie,
  MovieCrewRole,
  PageType,
  PerformanceGroup,
  SortType,
  TabType
} from '@usheru-hff/shared/domain';

import { Api } from './api';

export type AppType = 'widget' | 'cdp';
export interface Environment {
  envcustomizerenabled?: boolean;
  homePageConfig?: HomePageConfig;
  mode?: 'develop' | 'demo' | 'prod' | 'local' | 'test';
  appType: AppType;
  /**
   * In case of only want render MOVIE pages for the app. This will hide some element like
   * redirections like the ones to the home page.
   */
  appHasOnlyMovies?: boolean;
  api: Api;
  /**
   * Configuration for specific branded pages.
   * These parans should be provided by the movie
   * itself in the future and removed from here.
   */
  movieSiteConfig?: {
    [movie: string]: MovieSiteConfig;
  };

  /* By default the footer would be "tinted" using a shadow effect on the BrandedService. */
  movieAwarenessOriginalFooter?: boolean;
  movieAwarenessBackgroundImageFit?: boolean;
  movieAwarenessLogoExternalLink?: string;
  showBlueSky?: boolean;
  /**
   * Application with different versions based on country, adding a dimension to the just language selection.
   * Example: Scanbox Entertainment.
   */
  countryBasedApplication?: {
    enabled: boolean;
    default?: {
      countryCode: CountryCode;
      languageCode: LanguageCode;
    };
    defaultLocations?: Record<LanguageCode, string>;
  };

  defaultTheme?: Theme;
  authentication?: {
    type: 'page' | 'dialog';
  };
  community?: boolean;
  communityConfig?: {
    role?: 'film-interactions' | 'community-social';
    profileConfig?: {
      privacy?: boolean;
      avatar?: boolean;
      displayName?: boolean;
      terms?: boolean;
      name?: boolean;
      about?: boolean;
      followers?: boolean;
      following?: boolean;
      rating?: boolean;
      savedSections?: {
        wishlist?: boolean;
        hashtags?: boolean;
        collections?: boolean;
        platforms?: boolean;
        lists?: boolean;
      };
    };
    moviesCatalogConfig?: {
      movieCardCommunity?: boolean;
    };
    filmInteractionsConfig?: {
      watchedIcon?: 'eye' | 'check';
    };
    collectionsConfig?: {
      collectionCategoryTitle?: boolean;
      collectionImage?: boolean;
      collectionsSorting?: CollectionSortingOptions[];
      collectionsMovieSorting?: CollectionSortingOptions[];
    };
    userChipCompacted?: boolean;
  };
  /**
   * Sustitute the movie detail component with a custom one in the MoviePageResolver
   * Example value:
   * ```ts
   * () => import('./movie-detail-page/movie-detail-page.component').then(m => m.MovieDetailPageComponent);
   * ```
   */
  alternativeMovieDetail?: () => Promise<unknown>;
  stripe?: string;
  listLanguages?: string[];
  customMovies?: CustomMovie[];
  customPageParams?: CustomPageParams;
  movieDetail?: MovieDetailParams;
  customFeatures?: CustomFeatures;
  movieFilters?: MovieFilters;
  moreInfoParams?: MoreInfoParams;

  newsParams?: NewsParams;
  components?: EnvironmentComponents;
  siteName?: string;
  google?: Google;
  twitter?: Twitter;
  oAuthFacebook?: string;
  oAuthGoogle?: string;
  copyrightMsg?: string;
  channelTilePhoto?: string;
  defaultImagePlaceholder?: (channelService: Channel) => string;

  movieCrew?: {
    movieCrewRoleListing?: MovieCrewRole[];
    movieCrewPipe?: (crew: Crew[]) => Crew[];
  };
  /**
   * This is a function that will sort the queries to movies/ when the sort is locReleaseDate will apply this sorting, by default uses the function  movies.sort((a, b) => b.locReleaseDate - a.locReleaseDate)
   */
  locReleaseDateMoviesSortInCinemas?: (movies: Movie[]) => Movie[];
  locReleaseDateMoviesSortComingSoon?: (movies: Movie[]) => Movie[];

  hideShowtimeStreamingTabs?: TabType[];
  movieCardParams?: MovieCardParams;
  carouselTypeParams?: CarouselTypeParams;
  customSlidersParams?: CustomSlidersParams;
  templateRefs?: TemplateRefs;
  assetsRefs?: AssetsRefs;
  logosConfig?: LogosConfig;
  // if true will display the tab title of the showtime/streaming component always.
  showStreamingTitleAlways?: boolean;
  // This property is  meant to be used as an indicator to whether or not to inject
  // extra styles to configure (PROD-10630)
  setSwiperSlideHeightAuto?: boolean;

  showStreamingComponentAlways?: boolean;
  showShowtimesComponentAlways?: boolean;
  hideSubscriptionComponent?: boolean;

  /**
   * If true, the subscription service won't perform the POST request to the API.
   */
  avoidSubscriptionPostApiRequest?: boolean;
  // movieFilterBy is a optional filter for the movies page movie e.g. movie.tag?.contains == 'thesis'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  movieFilterBy?: any;
  // moviesPageCustomTags is a tag or list of tags that will be applyed to the getMovies query of the movies page e.g ['thesis']   or   ['thesis, dww']
  moviesPageCustomTags?: string[];
  moviesCatalogueConfig?: {
    searchByCrewKeywordDisabled?: boolean;
    movieCardsFullScreenWidth?: boolean;
  };
  searchMovieWidgetConfig?: {
    searchCompacted?: boolean;
    withFilters?: boolean;
    searchIconPosition?: 'left' | 'right';
    searchIcon?: 'pi-search' | 'pi-filter';
  };
  //genres filtering -> It is an array of genre slugs you want to be available to choose in the filter multiselect widget
  genresFiltering?: string[];
  // LanguagesPipe is a function that will transform the languages array from collections into something else and will return an array of Languages
  // it is used in the movies page.
  languagesPipe?: (languages: Observable<Language[]>) => Observable<Language[]>;
  // Countries Pipe is a function that will transform the countries array from collections into something else and will return an array of Countries
  // it is used in the movie detail page.
  countriesPipe?: (countries: Country[]) => Country[];
  languageSelectorEnglishName?: boolean;
  // if search component true then instead of allowing to search there, it will redirect to movies watch at home straight away
  searchComponentRedirect?: boolean;
  // Number news is to limit the number of news you want to display in the news feeder
  numberNews?: number;
  // News type is for showing usual news component or show it like a carousel
  newsViewTypeOnHomePage?: '' | 'carousel';
  newsViewTypeOnNewsPage?: '' | 'carousel';
  // Read More lenght is the max number of characters displayed to a paraghaph before adding the ... Read More btn (used in news component)
  readMoreLenght?: number | 'first-sentence';
  competitions?: boolean;
  // It is necesary for the enquiries widget on movie detail component
  enquiries?: boolean;
  fakeApiResponse?: ApiResponse[];
  channelCarouselImgRedirectOnClick?: boolean;
  channelCarouselDisableLooop?: boolean;
  showtimeStreamingConfig?: ShowtimeStreamingConfig;
  is12HourFormat?: boolean;
  /**
   * Will render the movie logo and release message if a  logo exists for the movie.
   */
  competitionHeader?: boolean;
  enableSalesAgent?: boolean;
  manualLanguageCode?: string;

  /**
   *  used for possible different function to 'getStarList' only for now in the search component (currently used for afi and nfts to look for alumnis only)
   */
  queryFunctionNames?: {
    talentQuery?: string;
  };

  /**
   * Will determine custom config params for a competition given a movie slug
   */
  competitionsConfig?: CompetitionConfig[];

  videoThumbnailCustomWidths?: {
    // breakpoint
    b: number | 'default';
    // width
    w: number;
  }[];

  /**
   * Change the competition url to attach to a custom pages.
   */
  competitionsCustom?: {
    [movieFriendlyUrl: string]: {
      bannerContent?: string;
      url?: string;
      qParams?: Params;
    };
  };

  /**
   * To use fake data.
   */
  placeholderData?: PlaceholderData;

  /**
   * Used to prevent some requests to the api.
   */
  preventRequest?: {
    attributes?: boolean;
    countries?: boolean;
    genres?: boolean;
    languages?: boolean;
    streamingCountries?: boolean;
  };

  /**
   * Determines the different sizes for img loading directive in the format:
   * (max-width: 576px) 90vw, (max-width: 768px) 50vw, (max-width: 1200px) 30vw, 25vw
   * witch means "until 576px img will take about 90 view width of the screen, etc. in the same order from mobile to desktop"
   */
  customResponsive?: CustomResponsive;

  /** Toogle  if show new version of some components
   */
  newComponents?: {
    showtimeStreamingInCinemas?: boolean;
  };

  performanceStartDateFilter?: Date;
  performanceQueryExtraFilter?: { [key: string]: string };
  performanceQueryResultFilter?: (performances: PerformanceGroup) => boolean;

  /**
   * setted when the app start
   */
  language?: string;

  /**
   * Over
   */
  customFrontendModules?: {
    [moduleName: string]: () => Promise<Type<CustomModuleComponent>>;
  };
}

export interface Theme {
  backgroundColor?: string;
  brandColor?: string;
  secondaryColor?: string;
  borderRadius?: string;
  textColor?: string;
  mainFont?: string;
  mainFontBold?: string;
}

export interface CustomResponsive {
  carouselMoviePortrait?: CustomResponsiveAttributes;
  carouselMovieLandscape?: CustomResponsiveAttributes;
  carouselGalleryPortrait?: CustomResponsiveAttributes;
  carouselGalleryLandscape?: CustomResponsiveAttributes;
  dateResponsiveOptions?: CarouselResponsiveOption[];
}

export type CollectionSortingOptions = 'title' | 'channelRating' | 'userRating' | 'popularity' | 'releaseDate';

export interface CustomResponsiveAttributes {
  sizes?: string;
  objectFit?: string;
  height?: string;
}
export interface MovieFilters {
  all?: MovieFilter;
  inCinemas?: MovieFilter;
  watchAtHome?: MovieFilter;
  comingSoon?: MovieFilter;
  atFestivals?: MovieFilter;
  tag?: MovieFilter;
}
export interface MovieFilter {
  film?: boolean;
  childFriendly?: boolean;
  location?: boolean;
  cinema?: boolean;
  streamingCountry?: boolean;
  streaming?: boolean;
  festival?: boolean;
  availability?: boolean;
  tagTypes?: 'tags' | 'allTags';
  extraFilters?: {
    languages?: boolean;
    country?: boolean;
    genres?: boolean;
    attribute?: boolean;
    date?: boolean;
    // if set to true award winner switch will present in more filters form
    awardWinner?: boolean;
    // if set to true festival type drowdown will be present in more filters form
    festivalType?: boolean;
    // of set tp true combo box of upcoming upcoming and past festivals will be present in more filters form
    upcomingFestivals?: boolean;
    // of set tp true combo box of past upcoming and past festivals will be present in more filters form
    pastFestivals?: boolean;
    // of set tp true combo box of ongoing upcoming and past festivals will be present in more filters form
    ongoingFestivals?: boolean;
    childFriendly?: boolean;
    tv?: boolean;

    // filter by channel tags
    tag?: boolean;
    // customTags is a list of strings that will count as tags on selection
    //(custom tags added here in environment as string[])
    customTag?: string[];
    // Not an actual filter but show as one. Only to watch at home
    luckyBtn?: boolean;
    year?: boolean;
    rightOwner?: boolean;
    category?: boolean;
  };
}
export interface Google {
  maps_key?: string;
}
export interface Twitter {
  site?: string;
  creator?: string;
}

/**
 *
 */
export interface CustomPageParams {
  //
  envcustomizerenabled?: boolean;
  // works is channelVideoEmbed is enabled
  channelVideoAutoplay?: boolean;
  channelVideoEmbed?: boolean;
  headerFixed?: boolean;
  headerSMsearchTop?: boolean;
  headerSocialLinks?: boolean;
  headerSMsearchCollapse?: boolean;
  headerSearchActive?: boolean;
  headerSMsearchActive?: boolean;
  headerSMtranslationCollapsed?: boolean;
  footerSocialLinks?: boolean;
  moviesCollapseFilters?: boolean;
  moviesCompactedFilters?: boolean;
  moviesCardsFlip?: boolean;
  moviesCardsFlipBackPortrait?: boolean;
  moviesCardsOnlyOneSide?: boolean;
  moviesInitialSortAll?: SortType[];
  moviesInitialSortInCinemas?: SortType[];
  moviesInitialSortWatchAtHome?: SortType[];
  moviesInitialSortComingSoon?: SortType[];
  moviesInitialSortTag?: SortType[];
  moviesCustomTagTopMessage?: { [tag: string]: string };
  moviesTypeMenu?: { name?: string; link: string; params?: object }[];
  movieDetailVideoEmbed?: boolean;
  movieDetailMuteVideo?: boolean;
  movieDetailAutoplayTrailer?: boolean;
  movieDetailShareInThumbnail?: boolean;
  movieDetailShowRightOwners?: boolean;
  movieDetailLaurelsEnabled?: boolean;
  movieDetailTMDBLogo?: '' | 'white';
  movieCreditsCollapse?: boolean;
  movieCreditsCollapseInitial?: boolean;
  movieCreditsProducingCompanies?: boolean;
  movieInfoCollapse?: boolean;
  movieInfoCollapseInitial?: boolean;
  movieInfoShowTPAffinity?: boolean;
  movieInfoShowRightOwners?: boolean;
  movieInfoModalPortrait?: boolean;
  videosGalleryTitles?: boolean;
  movieDetailContentAlternative?: boolean;
  movieDetailReviews?: boolean;
  movieDetailNoGallery?: boolean;
  movieDetailNoPosters?: boolean;
  movieDetailNoVideos?: boolean;
  movieDetailNoMovieLogo?: boolean;
  login?: boolean;
  translationHeader?: boolean;
  translationFooter?: boolean;
  favicon?: string;
  uPlayer?: boolean;
  seeMoreDisplay?: boolean;
  seeMoreType?: number;
  hideWatchNowCtaButton?: boolean;
  /** Will determine the position of the read more widget in movie detail, top means will be on the middle of the video thumbnail
   * and 'middle' means will be positioned in the video thumbnail controllers (in the same level than watch trailer btn)
   */
  seeMorePos?: 'top' | 'middle' | 'buttons' | 'none';
  // Make available the talent pages. If is it the header search should get the stars with a link to the talent page and not
  // the movies where participate.
  talentPages?: boolean;
  goToBottomHomePage?: boolean;

  /**
   * Force to show the widget with some local data.
   */
  placeholderData?: PlaceholderData;

  locationSelectorContainerType?: 'container-type-normal' | 'container-type-inline-size';
  showTrailerButton?: boolean;
}

/**
 * General-purpose custom feature flags/configs.
 */
export interface CustomFeatures {
  // have movie detail routes also in the form of '/movie-slug' along '/movie/movie-slug'
  nonPrefixedMovieDetailRoutes?: boolean;
  // adds a carousel with recomendations to the movie details
  movieDetailRecomendations?: CarouselParams;
  // to have a poster in the brandedPages movie detail
  brandedPageSynopsisPoster?: boolean;
}

export interface MoreInfoParams {
  releaseDate?: 'full' | 'year' | false;
  locReleaseDate?: 'full' | 'year' | false;
  releaseDateEmptyMessage?: string;
  certification?: boolean;
  countries?: boolean;
  languages?: boolean;
  duration?: boolean;
  genres?: boolean;
}
export interface CarouselTypeParams {
  // Config for each type
  movies?: CarouselParams;
  movieVideos?: CarouselParams;
  gallery?: CarouselParams;
  videos?: CarouselParams;
  posters?: CarouselParams;
  channel?: CarouselParams;
  moviesPortrait?: CarouselParams;
  moviesLandscape?: CarouselParams;
  collections?: CarouselParams;
}

//TODO deprecated. update proyects
export interface CustomSlidersParams {
  movieSlider?: SliderParams;
  movieDetailSlider?: SliderParams;
}

//TODO deprecated. update proyects
export interface SliderParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  breakpoints?: any;
  imgByPage?: number;
  dots?: boolean;
  arrowsTop?: boolean;
  arrowsTopMiddle?: boolean;
  arrowsSide?: boolean;
  arrowType?: 1 | 2 | 3 | 4 | 5;
}

export interface TemplateRefs {
  backCardExtraDescription?: string;
  inCinemasAddon?: string;
  branded?: BrandedTemplatesRefs;
  headerCustomLinkTemplate?: string; // Custom link that can be inserted in the header links
  footerCustomLinkTemplate?: string; // Custom link that can be inserted in the footer links
}

export interface BrandedTemplatesRefs {
  inCinemasAddon?: string;
}

export interface MovieCrewConfig {
  movieCrewRoleListing?: MovieCrewRole[];
  movieCrewPipe?: (crew: Crew[]) => Crew[];
}
export interface LogosConfig {
  header?: {
    logoLink?: string;
    logoFragment?: string;
    logoUrl?: string;
    width?: number;
    height?: number;
  };
  footer?: {
    logoLink?: string;
    logoUrl?: string;
    width?: number;
    height?: number;
    partnersText?: string;
    partners?: {
      logoClass?: string;
      logoAlt?: string;
      logoLink?: string;
      logoUrl?: string;
      width?: number;
      height?: number;
      logoLinkExternal?: boolean;
    }[];
  };
  channelVideo?: {
    logoLink?: string;
    logoUrl?: string;
    width?: number;
    height?: number;
  };
}

export interface ShowtimeStreamingConfig {
  priority?: PageType;
  priorityByMovie?: { slug: string; priority: PageType }[];
  performanceHourAttributes?: boolean;
  nearCitiesDisable?: boolean;
  isNewFlow?: boolean;
  numberOfCinemasOnFirstLoad?: number;
  newFlowAttributes?: ShowTimeStreamingNewFlowAttributes;
  demandItOnShowtimeStreamingAbsence?: boolean;
  // Not tested properly. Also is not applied by a comment.
  performancesGroupFilter?: (performancesGroup: PerformanceGroup, index: number, array: PerformanceGroup[]) => unknown;
  // Not tested properly. Also is not applied by a comment.
  performanceGroupFilterByMovie?: {
    [movieSlug: string]: (performancesGroup: PerformanceGroup, index: number, array: PerformanceGroup[]) => unknown;
  };
  //
  performancesAttributeFilter?: string;
  performancesAttributeFilterByMovie?: {
    [movieSlug: string]: string;
  };
}

export interface ShowTimeStreamingNewFlowAttributes {
  dateSelector?: NewFlowDateSelector;
}

export interface NewFlowDateSelector {
  animateOnDateSelect?: boolean;
  spaceBetweenDates?: number;
  enableBlurOnEdges?: boolean;
  breakDateInNewLines?: boolean;
  dateFormat?: NewFlowDateFormatOptions;
}

export enum NewFlowDateFormatOptions {
  MONTH_DATE_DAY = 'month-date-day',
  DAY_DATE_MONTH = 'day-date-month'
}

export interface PlaceholderData {
  assets?: string | boolean;
  movie?: string;
  movies?: string | boolean;
  performancesGroup?: string | boolean;
  nearestCities?: string | boolean;
  newsPosts?: string | boolean;
  streamingLinks?: string | boolean;
  socialFeed?: string | boolean;
  tags?: string | boolean;
  tagsTypes?: string | boolean;
}

/**
 * Variable to alternate some assets. The value if it is 'true' will spect a svg in a default url.
 */
export interface AssetsRefs {
  videoThumbnailPlayIcon?: AssetType;
}

export interface MovieCardParams {
  inCinemasDateFormat: 'short' | 'long';
}

export interface AssetType {
  width?: number;
  height?: number;
  /**
   * icon should be saved in the assets/icons folder of the application and the name only should be provided here
   * e.g. play-trailer-icon.svg
   */
  src: string;
}

export interface MovieDetailParams {
  hero?: MovieDetailHeroParams;
  moreInfo?: MoreInfoParams;
}

export interface MovieDetailHeroParams {
  embed?: boolean;
  autoplay?: boolean;
  /**
   * Enables a button to attach links to an external page where see the full video.
   * There are 3 conditions to show the button:
   *  - enable this param in the environment
   *  - the page must has the queryparam **fullVideo**
   *  - the movie must has the **fullVideoUrl**.
   */
  fullVideo?: boolean;

  /**
   * Enable show the "Watch Now" or "Book Now" button in the hero to scroll down.
   * This affects only to default movie detail pages.
   * To show this the movie needs *streamings* or *showtimes* in the movie.
   */
  showWatchNowCtaButton?: boolean;
}

export interface HomePageConfig {
  enableGroupSlidingForCarousels?: boolean;
}

export interface NewsParams {
  /**
   * If set empty '' it will default to channel default tile img
   */
  placeholderImg?: string;
}

/**
 * Params to be applied on pre-purchase-video-page
 */
export interface PrePurchasePageParams {
  continueWhenSelectVideo?: boolean;
  hideLanguageVideo?: boolean;
  hideQualityVideo?: boolean;
  hideSelectVideo?: boolean;
  hideAvailabilityDays?: boolean;
}

/**
 * Params to be applied on pre-purchase-video-page
 */
export interface MovieDetailInfoParams {
  showReleaseDate?: (infoParams?: MoreInfoParams, movie?: Movie) => boolean;
  showLocReleaseDate?: (infoParams?: MoreInfoParams, movie?: Movie) => boolean;
  showStreamingDate?: (movie?: Movie) => boolean;
  showLocStreamingDate?: (movie?: Movie) => boolean;
  /**
   * The filed will redirect to the movies page with a filter applied.
   * By default if will redirect to 'genres', 'languages' and 'countries'
   */
  enabledRedirections?: () => MoreInfoFields[];
}

export type MoreInfoFields = 'genres' | 'languages' | 'countries';

export interface UplayerParams {
  hidePriceOnWatchNowButton?: boolean;
}
export interface MovieCatalogueParams {
  filters?: {
    omitAddtionUserLocation?: boolean;
  };
}

export interface MovieFeedParams {
  /**
   * When searching the movies, the results when clicking `Load more` will be replaced or concatenated.
   * By default be used 'concatenate'.
   */
  paginationResults?: 'concatenate' | 'replace';
}

export interface WatchAtHomeParams {
  /**
   * Icons from primeicons. Styles must be imported previously.
   */
  dropdownIcon?: string | 'chevron-arrow-down' | null;
}

export interface ShowTimeStreamingSubscribeParams {
  hideSuccessMessage?: boolean;
}

// Create a configuration structure in JSON that allow a component to have a configuration,
// but allow to override it depending on 2 things
// 1) current route or
// 2) the current component being child of another kind of component (not implemented yet)
//
export type EnvironmentComponentConfig<T extends Record<string, any>> = Partial<T> & {
  pageOverrides?: { [route: string]: Partial<T> };
};

export interface EnvironmentComponents {
  prepurchasePage?: EnvironmentComponentConfig<PrePurchasePageParams>;
  movieDetailInfo?: EnvironmentComponentConfig<MovieDetailInfoParams>;
  uplayer?: EnvironmentComponentConfig<UplayerParams>;
  newsPage?: EnvironmentComponentConfig<NewsPageParams>;
  movieCatalogue?: EnvironmentComponentConfig<MovieCatalogueParams>;
  movieFeedParams?: EnvironmentComponentConfig<MovieFeedParams>;
  watchAtHome?: EnvironmentComponentConfig<WatchAtHomeParams>;
  showtimeStreamingSubscribe?: EnvironmentComponentConfig<ShowTimeStreamingSubscribeParams>;
}

export interface NewsPageParams {
  hideTitle?: boolean;
  hideSubtitle?: boolean;
  hideDate?: boolean;
  hideContent?: boolean;
}

export interface MovieSiteConfig {
  awarenessHideTitleLogo?: boolean;
  awarenessHideTitleText?: boolean;
  conversionHideTitleLogo?: boolean;
  conversionHideTitleText?: boolean;
  awanessHideReleaseMessage?: boolean;
  tileImageAspectRatio?: MovieSiteConfigAspectRatio;
  awarenessLogoPosition?: MovieSiteConfigAwarenessLogoPosition;
  awarenessDisableLandingGradient?: boolean;
  /**
   * Not compatible with releseMessagePosition
   */
  attachReleaseMessageToLogo?: boolean;
  /**
   * Not compatible with attachReleaseMessage
   */
  releseMessagePosition?: MovieSiteConfigHorizontalAlign;
  /**
   * @default true
   */
  landingFillScreen?: boolean;
  /**
   * By default it will take the header bg color from the App if it is setted.
   * This allows to change for cases where the headerImagesConfig is used.
   */
  headerBackgroundColor?: string;
  headerImagesConfig?: MovieSiteHeaderImagesConfig;
  disableImdb?: boolean;
  disableTmdb?: boolean;
}

export type MovieSiteConfigHorizontalAlign = 'left' | 'center' | 'right';
export type MovieSiteConfigVerticalAlign = 'middle' | 'bottom';

export interface MovieSiteConfigAspectRatio {
  w: number;
  h: number;
}

export interface MovieSiteConfigAwarenessLogoPosition {
  horizontal?: MovieSiteConfigHorizontalAlign;
  /**
   * Bottom is the default position
   */
  vertical?: MovieSiteConfigVerticalAlign;
  offset?: {
    x?: string;
    y?: string;
  };
}

export interface MovieSiteHeaderImagesConfig {
  sources?: MovieSiteHeaderImg[];
}
export interface MovieSiteHeaderImg {
  src: string;
  alt?: string;
  url?: string;
  externalLink?: boolean;
  /**
   * Required to get the image in the correct size.
   */
  width: number;
  /**
   * Required to get the image in the correct size.
   */
  height: number;
  /**
   * To what type will work. By default will be both.
   */
  filter?: 'awareness' | 'conversion';
  hideOnHeaderLg?: boolean;
  hideOnHeaderSm?: boolean;
  hideOnSideMenu?: boolean;
}
